
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import { AppDispatch, RootState } from "../../../../redux/store";
import {
  upsertCommercePartner,
  uploadCommercePartnerLogo,
  listenToUserCommercePartners,
} from "../../../../redux/commercePartnersSlice";
import { Camera, MapPin, Pencil, Link2, CheckCircle } from "lucide-react";

// ── API ──────────────────────────────────────────────────────────
const GENERATE_TOKEN_URL = "https://europe-west3-metarelm.cloudfunctions.net/generateToken";

// ── Wait for Google Maps ─────────────────────────────────────────
function waitForGoogleMaps(): Promise<void> {
  return new Promise((resolve) => {
    if ((window as any).google?.maps?.places) { resolve(); return; }
    const interval = setInterval(() => {
      if ((window as any).google?.maps?.places) { clearInterval(interval); resolve(); }
    }, 100);
  });
}

// ── Steps ────────────────────────────────────────────────────────
const STEPS = ["My Stores", "Identity", "Visuals", "Location", "Get Token"];

// ── Address ──────────────────────────────────────────────────────
interface AddressState {
  street: string; city: string; country: string;
  county: string; neighborhood: string;
  latitude: number | undefined; longitude: number | undefined;
}
const EMPTY_ADDRESS: AddressState = {
  street: "", city: "", country: "", county: "",
  neighborhood: "", latitude: undefined, longitude: undefined,
};

// ── Status helper ────────────────────────────────────────────────
const getStatus = (p: any) => {
  if (p.isComplete) return { label: "Active",  color: "#00c853" };
  if (p.isDraft)    return { label: "Draft",   color: "#9e9e9e" };
  return                   { label: "Pending", color: "#555"    };
};

// ── CSS ──────────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800;900&family=DM+Sans:wght@300;400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  input::placeholder { color: #B8B8B4; }
  ::-webkit-scrollbar { display: none; }
  @keyframes bzSpin { to { transform: rotate(360deg); } }
  @keyframes gradFlow { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
  @keyframes fadeUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
  input:focus { border-color: #6A00FF !important; box-shadow: 0 0 0 3px rgba(106,0,255,0.10) !important; outline: none; }
  .pac-container { font-family:'DM Sans',sans-serif!important; font-size:13px!important; border:1.5px solid #E6E6E4!important; border-top:none!important; box-shadow:0 8px 24px rgba(0,0,0,.08)!important; border-radius:0 0 12px 12px!important; z-index:99999!important; }
  .pac-item { padding:10px 14px!important; border-top:1px solid #F4F4F2!important; color:#2C2C2C!important; cursor:pointer!important; line-height:1.5!important; }
  .pac-item:hover,.pac-item-selected { background:#F5F0FF!important; }
  .pac-item-query { font-size:13px!important; color:#0A0A0A!important; font-weight:600!important; }
  .pac-icon { display:none!important; }
  .pac-matched { color:#6A00FF!important; font-weight:700!important; }
  .bt-card-btn { transition: all .18s; }
  .bt-card-btn:hover { opacity: 0.85; transform: translateY(-1px); }
`;

// ── Design tokens ────────────────────────────────────────────────
const T = {
  bg:        "#FFFFFF",
  bgSoft:    "#FAFAFA",
  bgMuted:   "#F4F4F2",
  black:     "#0A0A0A",
  blackMid:  "#1C1C1C",
  border:    "#E6E6E4",
  muted:     "#8A8A86",
  mutedSoft: "#B8B8B4",
  purple:    "#6A00FF",
  blue:      "#007BFF",
  pink:      "#FF00D4",
  gold:      "#C9A96E",
  goldLight: "#F0E2C8",
  goldBg:    "#FBF6EE",
  grad:      "linear-gradient(90deg,#007BFF,#6A00FF,#FF00D4)",
};

// ── Styles ───────────────────────────────────────────────────────
const S: Record<string, React.CSSProperties> = {
  page:      { background: T.bg, color: T.black, minHeight: "100vh", fontFamily: "'DM Sans',sans-serif", paddingBottom: 100 },
  header:    { position: "sticky", top: 0, zIndex: 100, background: T.bg, borderBottom: `1px solid ${T.border}`, padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" },
  brand:     { fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 16, letterSpacing: "0.2em", background: T.grad, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" },
  stepLbl:   { fontSize: 12, color: T.muted, fontFamily: "'Sora',sans-serif" },
  progBar:   { height: 3, background: "#f0f0f0" },
  progFill:  { height: "100%", transition: "width 0.4s ease" },
  body:      { maxWidth: 560, margin: "0 auto", padding: "40px 24px" },
  stepNum:   { fontSize: 9, fontWeight: 700, letterSpacing: "0.28em", color: T.muted, fontFamily: "'Sora',sans-serif", marginBottom: 12, display: "flex", alignItems: "center", gap: 10 },
  editBadge: { fontSize: 8, letterSpacing: "0.18em", color: "#fff", background: T.gold, padding: "2px 7px", borderRadius: 4, fontFamily: "'Sora',sans-serif", fontWeight: 700 },
  h1:        { fontFamily: "'Sora',sans-serif", fontSize: 26, fontWeight: 800, color: T.black, marginBottom: 6, lineHeight: 1.15 },
  sub:       { fontSize: 13, color: T.muted, marginBottom: 28, lineHeight: 1.7, fontWeight: 300 },
  lbl:       { display: "block", fontSize: 9, fontWeight: 700, letterSpacing: "0.2em", color: T.muted, fontFamily: "'Sora',sans-serif", marginBottom: 7, textTransform: "uppercase" },
  input:     { width: "100%", padding: "13px 16px", background: T.bg, border: `1.5px solid ${T.border}`, borderRadius: 12, color: T.black, fontSize: 14, fontFamily: "'DM Sans',sans-serif", outline: "none", boxSizing: "border-box", marginBottom: 18, transition: "border-color 0.2s, box-shadow 0.2s" },
  errBox:    { marginTop: 14, background: "#fff5f5", border: "1px solid #fcc", borderRadius: 10, padding: "12px 16px", color: "#c0392b", fontSize: 13 },
  overlay:   { position: "fixed", inset: 0, background: "rgba(255,255,255,0.92)", backdropFilter: "blur(8px)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16 },
  footer:    { position: "fixed", bottom: 0, left: 0, right: 0, background: "rgba(255,255,255,0.96)", backdropFilter: "blur(12px)", borderTop: `1px solid ${T.border}`, padding: "14px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", zIndex: 100 },
  backBtn:   { padding: "11px 24px", borderRadius: 30, background: T.bgMuted, color: T.muted, border: `1px solid ${T.border}`, cursor: "pointer", fontFamily: "'Sora',sans-serif", fontWeight: 600, fontSize: 13 },
  nextBtn:   { padding: "12px 32px", borderRadius: 30, background: "linear-gradient(90deg,#007BFF,#6A00FF)", color: "#fff", border: "none", fontWeight: 700, fontFamily: "'Sora',sans-serif", fontSize: 14, cursor: "pointer" },
  nextOff:   { padding: "12px 32px", borderRadius: 30, background: "#f0f0f0", color: "#bbb", border: "none", fontWeight: 700, fontFamily: "'Sora',sans-serif", fontSize: 14, cursor: "not-allowed" },
  newBtn:    { width: "100%", padding: "14px 0", borderRadius: 14, background: "linear-gradient(90deg,#007BFF,#6A00FF)", color: "#fff", border: "none", fontWeight: 800, fontFamily: "'Sora',sans-serif", fontSize: 15, cursor: "pointer", marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 },
  // list card
  lCard:     { display: "flex", alignItems: "center", gap: 12, borderRadius: 14, border: `1.5px solid ${T.border}`, background: T.bgSoft, padding: "12px 14px", marginBottom: 10, transition: "all 0.2s", position: "relative" },
  lCardOn:   { border: "2px solid #6A00FF", background: "linear-gradient(135deg,#f5f0ff,#ede0ff)", boxShadow: "0 0 14px rgba(106,0,255,0.18)" },
  lThumb:    { width: 52, height: 52, borderRadius: 10, objectFit: "cover", flexShrink: 0, background: "#ddd", display: "block" },
  lInfo:     { flex: 1, minWidth: 0 },
  lTitle:    { fontSize: 13, fontWeight: 700, color: T.black, marginBottom: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  lSub:      { fontSize: 11, color: T.muted },
  lBadge:    { borderRadius: 20, padding: "3px 10px", fontSize: 10, fontWeight: 700, color: "#fff", flexShrink: 0 },
  // logo
  logoWrap:  { width: 120, height: 120, border: `1.5px dashed ${T.border}`, borderRadius: 14, background: "#f8f8ff", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, overflow: "hidden", position: "relative", transition: "border-color 0.2s" },
  // address
  addrRow:   { display: "flex", border: `1.5px solid ${T.border}`, borderRadius: 12, background: T.bgSoft, marginBottom: 16, overflow: "hidden" },
  addrIcon:  { padding: "0 13px", display: "flex", alignItems: "center", background: T.bgMuted, borderRight: `1px solid ${T.border}`, color: T.muted, flexShrink: 0 },
  addrInput: { flex: 1, border: "none", outline: "none", padding: "13px 14px", fontSize: 14, color: T.black, background: "transparent", fontFamily: "'DM Sans',sans-serif" },
  fieldGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 },
  fieldItem: { background: T.bgMuted, border: `1px solid ${T.border}`, borderRadius: 10, padding: "10px 14px" },
  fieldLbl:  { fontSize: 9, fontWeight: 700, letterSpacing: "0.15em", color: T.muted, fontFamily: "'Sora',sans-serif", marginBottom: 4, textTransform: "uppercase" as const },
  fieldVal:  { fontSize: 13, color: T.black },
  // token result
  summCard:  { background: T.bgSoft, border: `1.5px solid ${T.border}`, borderRadius: 14, overflow: "hidden", marginBottom: 20 },
  tokenBox:  { background: "#f8f9fa", border: `1.5px solid ${T.border}`, borderRadius: 12, padding: "12px 16px", marginBottom: 12 },
  copyBtn:   { border: "none", borderRadius: 8, padding: "6px 12px", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 5, transition: "background 0.2s" },
  finalBtn:  { width: "100%", padding: "14px 0", borderRadius: 14, background: T.grad, color: "#fff", border: "none", fontWeight: 800, fontFamily: "'Sora',sans-serif", fontSize: 15, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 20, boxShadow: "0 4px 20px rgba(106,0,255,0.25)" },
  regenBtn:  { width: "100%", marginTop: 10, padding: "10px 0", borderRadius: 12, background: "transparent", border: `1.5px solid ${T.border}`, color: T.muted, fontWeight: 600, fontFamily: "'Sora',sans-serif", fontSize: 12, cursor: "pointer" },
};

// ════════════════════════════════════════════════════════════════
//  COMPONENT
// ════════════════════════════════════════════════════════════════
const BusinessToken: React.FC = () => {
  const navigate     = useNavigate();
  const dispatch     = useDispatch<AppDispatch>();
  const user         = useSelector((s: RootState) => s.auth.user);
  const { partners } = useSelector((s: RootState) => s.commercePartners);
  const userId       = user?.userId ?? "";

  const logoRef         = useRef<HTMLInputElement>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);

  const [step,       setStep]       = useState(0);
  const [partnerId,  setPartnerId]  = useState<string | null>(null);
  const [isEdit,     setIsEdit]     = useState(false);   // ← tracks edit vs new
  const [busy,       setBusy]       = useState(false);
  const [overlayMsg, setOverlayMsg] = useState("");
  const [errMsg,     setErrMsg]     = useState("");
  const [mapsReady,  setMapsReady]  = useState(!!(window as any).google?.maps?.places);
  const [uploading,  setUploading]  = useState(false);
  const [success,    setSuccess]    = useState(false);

  // Step 1 — Identity
  const [storeName,   setStoreName]   = useState("");
  // Step 2 — Visuals
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFile,    setLogoFile]    = useState<File | null>(null);
  // Step 3 — Location
  const [address,     setAddress]     = useState<AddressState>(EMPTY_ADDRESS);
  // Step 4 — Token
  const [claimUrl,    setClaimUrl]    = useState("");
  const [claimMsg,    setClaimMsg]    = useState("");
  const [copiedUrl,   setCopiedUrl]   = useState(false);
  const [copiedMsg,   setCopiedMsg]   = useState(false);

  // ── listeners ──────────────────────────────────────────────────
  useEffect(() => {
    if (userId) dispatch(listenToUserCommercePartners(userId) as any);
  }, [userId, dispatch]);

  useEffect(() => {
    if (!mapsReady) waitForGoogleMaps().then(() => setMapsReady(true));
  }, []);

  // ── Autocomplete — step 3 ──────────────────────────────────────
  useEffect(() => {
    if (step !== 3 || !mapsReady) return;
    const tid = setTimeout(() => {
      const input = addressInputRef.current;
      if (autocompleteRef.current) {
        (window as any).google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
      if (!input) return;
      const ac = new (window as any).google.maps.places.Autocomplete(input, {
        types: ["geocode"],
        fields: ["address_components", "geometry"],
      });
      ac.addListener("place_changed", () => {
        const place = ac.getPlace();
        if (!place?.address_components) return;
        const comps = place.address_components as Array<{ types: string[]; long_name: string }>;
        const get   = (t: string) => comps.find(c => c.types.includes(t))?.long_name ?? "";
        const resolved: AddressState = {
          street:       `${get("street_number")} ${get("route")}`.trim(),
          city:         get("locality") || get("administrative_area_level_2"),
          neighborhood: get("sublocality") || get("neighborhood"),
          country:      get("country"),
          county:       get("administrative_area_level_1"),
          latitude:     place.geometry?.location?.lat(),
          longitude:    place.geometry?.location?.lng(),
        };
        setAddress(resolved);
        if (partnerId) {
          dispatch(upsertCommercePartner({ brandId: partnerId, data: { address: resolved } }) as any);
        }
      });
      autocompleteRef.current = ac;
    }, 150);
    return () => clearTimeout(tid);
  }, [step, mapsReady, partnerId]);

  useEffect(() => {
    if (step !== 3 && autocompleteRef.current) {
      (window as any).google?.maps?.event?.clearInstanceListeners?.(autocompleteRef.current);
      autocompleteRef.current = null;
    }
  }, [step]);

  // ── Success redirect ───────────────────────────────────────────
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => { setSuccess(false); setIsEdit(false); setStep(0); }, 1800);
    return () => clearTimeout(t);
  }, [success]);

  // ── Populate from existing partner (edit mode) ─────────────────
  const populateFromPartner = (p: any, goToStep: number) => {
    setPartnerId(p.brandId ?? p.partnerId ?? null);
    setIsEdit(true);
    setStoreName(p.name ?? "");
    setLogoPreview(p.image ?? null);
    setLogoFile(null);
    setAddress(p.address ? {
      street:       p.address.street       ?? "",
      city:         p.address.city         ?? "",
      country:      p.address.country      ?? "",
      county:       p.address.county       ?? "",
      neighborhood: p.address.neighborhood ?? "",
      latitude:     p.address.latitude,
      longitude:    p.address.longitude,
    } : EMPTY_ADDRESS);
    setClaimUrl(""); setClaimMsg(""); setErrMsg("");
    setStep(goToStep);
  };

  // ── Start new store ────────────────────────────────────────────
  const startNew = () => {
    setPartnerId(null);
    setIsEdit(false);
    setStoreName(""); setLogoPreview(null); setLogoFile(null);
    setAddress(EMPTY_ADDRESS);
    setClaimUrl(""); setClaimMsg(""); setErrMsg("");
    setStep(1);
  };

  // ── Back nav ───────────────────────────────────────────────────
  const handleBack = () => {
    if (step === 0) { navigate(-1); return; }
    if (step === 1) { setIsEdit(false); setStep(0); return; }
    setStep(s => s - 1);
  };

  // ── validation ─────────────────────────────────────────────────
  const isValid = () => {
    if (step === 1) return !!storeName.trim();
    return true;
  };

  // ── handleNext ─────────────────────────────────────────────────
  const handleNext = async () => {
    if (!isValid()) return;
    setBusy(true); setErrMsg("");
    try {
      // Step 1 — save / upsert partner (passes existing id on edit)
      if (step === 1) {
        setOverlayMsg("Saving…");
        const r = await dispatch(upsertCommercePartner({
          brandId: partnerId ?? undefined,
          data: { name: storeName, isDraft: true, isComplete: false, createdVia: "businessToken" },
        }) as any).unwrap();
        setPartnerId(r.brandId ?? r.partnerId ?? null);
      }
      // Step 2 — upload logo if a new file was chosen
      if (step === 2 && logoFile && partnerId) {
        setOverlayMsg("Uploading logo…");
        setUploading(true);
        await dispatch(uploadCommercePartnerLogo({ brandId: partnerId, file: logoFile }) as any).unwrap();
        setUploading(false);
      }
      // Step 3 — finalise before token step
      if (step === 3 && partnerId) {
        setOverlayMsg("Finalising…");
        await dispatch(upsertCommercePartner({
          brandId: partnerId,
          data: {
            address:    address.city ? address : undefined,
            isDraft:    false,
            isComplete: true,
          },
        }) as any).unwrap();
      }
      setStep(s => s + 1);
    } catch (e: any) {
      setErrMsg(e.message || "Something went wrong");
    } finally {
      setBusy(false); setOverlayMsg(""); setUploading(false);
    }
  };

  // ── generate token ─────────────────────────────────────────────
  const handleGenerateToken = async () => {
    if (!partnerId) return;
    setBusy(true); setOverlayMsg("Generating claim link…"); setErrMsg("");
    try {
      const res  = await fetch(GENERATE_TOKEN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ brandId: partnerId, type: "business" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to generate token");
      setClaimUrl(data.claimUrl);
      setClaimMsg(data.message);
    } catch (e: any) {
      setErrMsg(e.message || "Failed to generate token");
    } finally {
      setBusy(false); setOverlayMsg("");
    }
  };

  const copy = (text: string, which: "url" | "msg") => {
    navigator.clipboard.writeText(text);
    if (which === "url") { setCopiedUrl(true); setTimeout(() => setCopiedUrl(false), 2000); }
    else                 { setCopiedMsg(true); setTimeout(() => setCopiedMsg(false), 2000); }
  };

  const openGmail = () => {
    const subject = encodeURIComponent(`${storeName} — Claim your business on Metarelm`);
    const body    = encodeURIComponent(claimMsg);
    window.open(`https://mail.google.com/mail/u/0/?fs=1&su=${subject}&body=${body}&tf=cm`, "_blank");
  };

  // ── step label helper ──────────────────────────────────────────
  const StepNum = ({ n }: { n: number }) => (
    <div style={S.stepNum}>
      <span>STEP 0{n} / 04</span>
      {isEdit && <span style={S.editBadge}>EDITING</span>}
    </div>
  );

  // ════════════════════════════════════════════════════════════════
  //  RENDER STEPS
  // ════════════════════════════════════════════════════════════════
  const renderStep = () => {

    // ── STEP 0: Store list ────────────────────────────────────────
    if (step === 0) return (
      <div style={{ animation: "fadeUp .25s ease" }}>
        <p style={S.h1}>My Stores</p>
        <p style={S.sub}>Select a store to edit or generate a claim token — or add a new business.</p>

        {/* Add Business button */}
        <button style={S.newBtn} onClick={startNew}>＋ Add Business</button>

        {partners.length === 0 ? (
          <p style={{ color: T.muted, fontSize: 13, textAlign: "center", padding: "32px 0" }}>
            No stores yet — add one above.
          </p>
        ) : partners.map((p: any) => {
          const pid = p.brandId ?? p.partnerId;
          const { label, color } = getStatus(p);
          return (
            <div key={pid} style={{ ...S.lCard, ...(partnerId === pid && isEdit ? S.lCardOn : {}) }}>
              {/* Logo */}
              {p.image
                ? <img src={p.image} alt="" style={S.lThumb as React.CSSProperties}/>
                : <div style={{ ...S.lThumb as React.CSSProperties, background: "#ede8ff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>🏪</div>
              }

              {/* Info */}
              <div style={S.lInfo}>
                <div style={S.lTitle}>{p.name || "Unnamed Store"}</div>
                <div style={S.lSub}>{[p.address?.city, p.address?.country].filter(Boolean).join(", ") || "No address"}</div>
                <div style={{ marginTop: 4 }}>
                  <span style={{ ...S.lBadge, background: color }}>{label}</span>
                </div>
              </div>

              {/* Action buttons */}
              <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0 }}>
                {/* Edit — prefills form and enters at step 1 */}
                <button
                  className="bt-card-btn"
                  onClick={() => populateFromPartner(p, 1)}
                  style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 20, background: T.bgMuted, border: `1px solid ${T.border}`, color: T.black, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "'Sora',sans-serif", whiteSpace: "nowrap" }}
                >
                  <Pencil size={11}/> Edit
                </button>
                {/* Token — skips to step 4 with data prefilled */}
                <button
                  className="bt-card-btn"
                  onClick={() => populateFromPartner(p, 4)}
                  style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 20, background: "linear-gradient(90deg,#6A00FF,#FF00D4)", border: "none", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "'Sora',sans-serif", whiteSpace: "nowrap" }}
                >
                  <Link2 size={11}/> Token
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );

    // ── STEP 1: Identity ──────────────────────────────────────────
    if (step === 1) return (
      <div style={{ animation: "fadeUp .25s ease" }}>
        <StepNum n={1}/>
        <h2 style={S.h1}>{isEdit ? "Edit Store Name" : "Store Identity"}</h2>
        <p style={S.sub}>
          {isEdit
            ? "Update your store name — changes save as you progress."
            : "Choose a name that represents this commerce brand to customers."}
        </p>
        <label style={S.lbl}>Store Name ✦</label>
        <input
          style={S.input}
          placeholder="e.g. Horizon Market"
          value={storeName}
          onChange={e => setStoreName(e.target.value)}
        />
        {errMsg && <div style={S.errBox}>⚠️ {errMsg}</div>}
      </div>
    );

    // ── STEP 2: Visuals ───────────────────────────────────────────
    if (step === 2) return (
      <div style={{ animation: "fadeUp .25s ease" }}>
        <StepNum n={2}/>
        <h2 style={S.h1}>{isEdit ? "Edit Logo" : "Store Logo"}</h2>
        <p style={S.sub}>
          {isEdit
            ? "Replace the logo or keep the current one and continue."
            : "Upload the store logo. This is what customers see first. (Optional)"}
        </p>

        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
          <label
            style={{ ...S.logoWrap, cursor: partnerId ? "pointer" : "not-allowed", opacity: partnerId ? 1 : 0.5 }}
            onClick={e => { if (!partnerId) e.preventDefault(); }}
          >
            {logoPreview
              ? <img src={logoPreview} alt="logo" style={{ width: "100%", height: "100%", objectFit: "cover" }}/>
              : <>
                  <Camera size={28} color={T.muted}/>
                  <span style={{ fontSize: 9, color: T.muted, fontFamily: "'Sora',sans-serif", letterSpacing: "0.1em" }}>UPLOAD LOGO</span>
                </>
            }
            {uploading && (
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.75)" }}>
                <div style={{ width: 24, height: 24, borderRadius: "50%", border: `3px solid ${T.purple}`, borderTopColor: "transparent", animation: "bzSpin 0.8s linear infinite" }}/>
              </div>
            )}
            <input
              ref={logoRef} type="file" hidden accept="image/*"
              onChange={e => {
                const f = e.target.files?.[0];
                if (!f || !partnerId) return;
                setLogoFile(f);
                setLogoPreview(URL.createObjectURL(f));
              }}
            />
          </label>

          {/* Edit mode: existing logo loaded hint */}
          {isEdit && logoPreview && !logoFile && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "#f0eaff", border: "1px solid #d5c5ff", borderRadius: 10 }}>
              <CheckCircle size={12} color={T.purple}/>
              <span style={{ fontSize: 11, color: "#5a2fa0", fontFamily: "'Sora',sans-serif" }}>Current logo loaded — tap to replace</span>
            </div>
          )}

          {/* New logo chosen */}
          {logoFile && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "#f0eaff", border: "1px solid #d5c5ff", borderRadius: 10 }}>
              <CheckCircle size={12} color={T.purple}/>
              <span style={{ fontSize: 11, color: "#5a2fa0", fontFamily: "'Sora',sans-serif" }}>New logo selected — will upload on continue</span>
            </div>
          )}

          {/* No partner yet (shouldn't normally happen but safety net) */}
          {!partnerId && (
            <p style={{ fontSize: 11, color: T.mutedSoft, fontFamily: "'Sora',sans-serif" }}>Save store name first to unlock logo upload</p>
          )}

          {/* New store, no logo chosen yet */}
          {partnerId && !isEdit && !logoPreview && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "#f0eaff", border: "1px solid #d5c5ff", borderRadius: 10 }}>
              <CheckCircle size={12} color={T.purple}/>
              <span style={{ fontSize: 11, color: "#5a2fa0", fontFamily: "'Sora',sans-serif" }}>Store saved — you can now upload a logo</span>
            </div>
          )}
        </div>
      </div>
    );

    // ── STEP 3: Location ──────────────────────────────────────────
    if (step === 3) return (
      <div style={{ animation: "fadeUp .25s ease" }}>
        <StepNum n={3}/>
        <h2 style={S.h1}>{isEdit ? "Edit Location" : "Location"}</h2>
        <p style={S.sub}>
          {isEdit
            ? "Update the store address or keep the current location and continue."
            : "Where is this store based? This helps match it with nearby customers. (Optional)"}
        </p>

        {!mapsReady && (
          <div style={{ fontSize: 11, color: T.purple, background: "#f0eaff", border: "1px solid #d5c5ff", borderRadius: 10, padding: "10px 14px", marginBottom: 14 }}>
            Loading address search…
          </div>
        )}

        <label style={S.lbl}>Business Address</label>
        <div style={S.addrRow}>
          <div style={S.addrIcon}><MapPin size={14}/></div>
          <input
            ref={addressInputRef}
            value={address.street}
            onChange={e => setAddress(a => ({ ...a, street: e.target.value }))}
            placeholder={mapsReady ? "Start typing your address…" : "Loading maps…"}
            autoComplete="off"
            style={{ ...S.addrInput, pointerEvents: mapsReady ? "auto" : "none", opacity: mapsReady ? 1 : 0.5 }}
          />
        </div>

        {address.city && (
          <div style={S.fieldGrid}>
            {[
              { label: "City",          val: address.city         },
              { label: "Country",       val: address.country      },
              { label: "Region",        val: address.county       },
              { label: "Neighbourhood", val: address.neighborhood },
            ].filter(f => f.val).map(f => (
              <div key={f.label} style={S.fieldItem}>
                <div style={S.fieldLbl}>{f.label}</div>
                <div style={S.fieldVal}>{f.val}</div>
              </div>
            ))}
            {address.latitude !== undefined && (
              <div style={{ ...S.fieldItem, gridColumn: "1 / -1" }}>
                <div style={S.fieldLbl}>Coordinates</div>
                <div style={{ ...S.fieldVal, fontSize: 11, color: T.muted }}>
                  {address.latitude?.toFixed(6)}, {address.longitude?.toFixed(6)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );

    // ── STEP 4: Get Token ─────────────────────────────────────────
    if (step === 4) return (
      <div style={{ animation: "fadeUp .25s ease" }}>
        <StepNum n={4}/>
        <h2 style={S.h1}>Get Claim Token</h2>
        <p style={S.sub}>Generate a unique claim link to send to the business owner. One click creates their account — completely free.</p>

        {/* Store preview card */}
        <div style={S.summCard}>
          <div style={{ height: 80, background: "linear-gradient(135deg,#f0eaff,#e8f0ff)", position: "relative" }}>
            {logoPreview && (
              <div style={{ position: "absolute", bottom: -24, left: 18, width: 48, height: 48, background: "#fff", border: "2px solid #fff", overflow: "hidden", boxShadow: "0 2px 8px rgba(106,0,255,0.2)", borderRadius: 10 }}>
                <img src={logoPreview} alt="logo" style={{ width: "100%", height: "100%", objectFit: "cover" }}/>
              </div>
            )}
          </div>
          <div style={{ padding: logoPreview ? "34px 18px 18px" : "18px" }}>
            <p style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 16, color: T.black, marginBottom: 4 }}>{storeName}</p>
            {address.city && (
              <p style={{ fontSize: 12, color: T.muted }}>
                📍 {[address.city, address.country].filter(Boolean).join(", ")}
              </p>
            )}
          </div>
        </div>

        {/* Generate button */}
        {!claimUrl && (
          <button style={S.finalBtn} onClick={handleGenerateToken} disabled={busy}>
            <Link2 size={16}/> Generate Claim Link
          </button>
        )}

        {/* Token result */}
        {claimUrl && (
          <div style={{ marginTop: 20 }}>
            {/* Claim URL */}
            <div style={S.tokenBox}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: T.purple, textTransform: "uppercase", letterSpacing: "0.07em" }}>Claim URL</span>
                <button
                  onClick={() => copy(claimUrl, "url")}
                  style={{ ...S.copyBtn, background: copiedUrl ? "#00c853" : T.black }}
                >
                  {copiedUrl ? "✓ Copied" : "📋 Copy"}
                </button>
              </div>
              <p style={{ fontSize: 12, color: "#555", wordBreak: "break-all", margin: 0, fontFamily: "monospace" }}>{claimUrl}</p>
            </div>

            {/* Message */}
            <div style={S.tokenBox}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: T.purple, textTransform: "uppercase", letterSpacing: "0.07em" }}>Message</span>
                <button
                  onClick={() => copy(claimMsg, "msg")}
                  style={{ ...S.copyBtn, background: copiedMsg ? "#00c853" : T.black }}
                >
                  {copiedMsg ? "✓ Copied" : "📋 Copy"}
                </button>
              </div>
              <pre style={{ fontSize: 12, color: "#444", whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0, fontFamily: "'DM Sans',sans-serif", lineHeight: 1.6, maxHeight: 220, overflowY: "auto" }}>
                {claimMsg}
              </pre>
            </div>

            {/* Gmail */}
            <button
              onClick={openGmail}
              style={{ width: "100%", padding: "14px 0", borderRadius: 12, background: "#fff", border: "2px solid #ea4335", color: "#ea4335", fontWeight: 800, fontFamily: "'Sora',sans-serif", fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}
            >
              <svg width="20" height="20" viewBox="0 0 48 48" fill="none">
                <path d="M6 36V12l18 13L42 12v24" stroke="#ea4335" strokeWidth="3" strokeLinejoin="round"/>
                <path d="M6 12l18 13 18-13" stroke="#ea4335" strokeWidth="3" strokeLinejoin="round"/>
                <rect x="4" y="10" width="40" height="28" rx="3" stroke="#ea4335" strokeWidth="3"/>
              </svg>
              Open in Gmail
            </button>

            {/* Regenerate */}
            <button style={S.regenBtn} onClick={handleGenerateToken}>
              ↻ Regenerate new link
            </button>
          </div>
        )}

        {errMsg && <div style={S.errBox}>⚠️ {errMsg}</div>}
      </div>
    );
  };

  // ── Success overlay ────────────────────────────────────────────
  const SuccessOverlay = () => (
    <div style={{ position: "absolute", inset: 0, background: T.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", zIndex: 20, gap: 16, padding: 32 }}>
      <div style={{ width: 72, height: 72, borderRadius: "50%", border: `2px solid ${T.black}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CheckCircle size={34} color={T.black}/>
      </div>
      <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 26, fontWeight: 800, color: T.black, margin: 0 }}>
        {isEdit ? "Changes Saved" : "Store Saved"}
      </h2>
      <p style={{ fontSize: 12, color: T.muted, fontFamily: "'DM Sans',sans-serif", textAlign: "center", margin: 0, lineHeight: 1.7 }}>
        {isEdit ? "Your store has been updated. Returning to list…" : "Returning to store list…"}
      </p>
      <div style={{ width: 160, height: 2, background: T.bgMuted, overflow: "hidden", marginTop: 8 }}>
        <div style={{ height: "100%", background: T.black, width: "100%", transition: "width 1.4s linear" }}/>
      </div>
    </div>
  );

  const progress = step === 0 ? 0 : ((step - 1) / (STEPS.length - 2)) * 100;
  const isFinal  = step === STEPS.length - 1;

  // Progress bar colour: gold when editing, gradient otherwise
  const progFillStyle: React.CSSProperties = {
    ...S.progFill,
    width: `${progress}%`,
    background: isEdit ? T.gold : T.grad,
  };

  // ════════════════════════════════════════════════════════════════
  //  ROOT RENDER
  // ════════════════════════════════════════════════════════════════
  return (
    <div style={S.page}>
      <style>{CSS}</style>

      {/* Busy overlay */}
      {busy && (
        <div style={S.overlay}>
          <CircularProgress size={52} sx={{ color: T.purple }}/>
          <p style={{ color: T.muted, fontSize: 13, fontFamily: "'Sora',sans-serif", letterSpacing: "0.1em" }}>
            {overlayMsg || "SAVING…"}
          </p>
        </div>
      )}

      {/* Header */}
      <div style={S.header}>
        <span style={S.brand}>METARELM</span>
        <span style={S.stepLbl}>
          {STEPS[step]}{isEdit && step > 0 ? " · Editing" : ""} · {step + 1}/{STEPS.length}
        </span>
        <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
          {STEPS.map((_, i) => (
            <div key={i} style={{
              width: i === step ? 20 : 8, height: 8, borderRadius: 999,
              background: i < step
                ? (isEdit ? T.gold : T.purple)
                : i === step ? T.pink : "#ebebeb",
              transition: "all 0.3s",
            }}/>
          ))}
        </div>
      </div>

      {/* Progress bar */}
      <div style={S.progBar}>
        <div style={progFillStyle}/>
      </div>

      {/* Body */}
      <div style={{ ...S.body, position: "relative" }}>
        {renderStep()}
        {success && <SuccessOverlay/>}
      </div>

      {/* Footer */}
      <div style={S.footer}>
        <button style={S.backBtn} onClick={handleBack}>
          ← {step === 0 ? "Cancel" : "Back"}
        </button>

        {step === 0 ? (
          <span style={{ fontSize: 12, color: T.muted }}>tap a store or add new ↑</span>
        ) : isFinal ? (
          <button style={S.nextBtn} onClick={() => { setIsEdit(false); setStep(0); }}>
            ← Back to Stores
          </button>
        ) : (
          <button
            style={isValid() ? S.nextBtn : S.nextOff}
            disabled={!isValid() || busy}
            onClick={handleNext}
          >
            {step === STEPS.length - 2
              ? (isEdit ? "Save Changes →" : "Finalise →")
              : "Continue →"}
          </button>
        )}
      </div>
    </div>
  );
};

export default BusinessToken;